import { Fragment } from 'react'
import { Globe } from 'lucide-react'
import ShapeRenderer from '@/components/app/shapes/shape-renderer'
import { ShapeTextEditor } from '@/components/app/shapes/shape-text-editor'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { defaultShapeTextParagraphs, hasShapeTextContent } from '@/lib/shapes/shape-text-frame'
import type { DragCreateState } from '../types/drag-create-state'

/** Accessible name of the Shift/Alt badge shown while drag-creating a shape. */
function resolveDragModifierBadgeLabel(dragCreate: DragCreateState | null): string {
  if (dragCreate?.shiftKey && dragCreate?.altKey) {
    return 'Draw from center, constrain proportions one to one'
  }

  return dragCreate?.shiftKey ? 'Constrain proportions one to one' : 'Draw from center'
}

/**
 * What the shape tool and Mirror Move draw over the document while in use: the drag-to-create
 * preview, the Mirror Move ghosts, the Alt center crosshair and the Shift/Alt modifier badge.
 */
export function DragCreateAdornments({
  dragPreviewShape,
  mirrorMovePreviewShapes,
  dragCreate,
  scale,
}: {
  dragPreviewShape: ShapeData | null
  mirrorMovePreviewShapes: ShapeData[]
  dragCreate: DragCreateState | null
  scale: number
}) {
  return (
    <>
      {/* Drag-to-create preview */}
      {dragPreviewShape && (
        <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 5 }}>
          <ShapeRenderer shape={dragPreviewShape} canvasZoom={scale} />
        </div>
      )}
      {/* Mirror Move hover preview: ghost of the mirrored position(s), including any
          text typed inside the shape (`paragraphs`) — `ShapeRenderer` itself
          never draws shape text (that's `ShapeTextEditor`'s job even in display mode,
          same as the real paint loop in `ShapeManager`), so the ghost needs both. */}
      {mirrorMovePreviewShapes.length > 0 && (
        <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 5 }}>
          {mirrorMovePreviewShapes.map((previewShape) => {
            const previewParagraphs = previewShape.paragraphs
            const showPreviewText =
              previewParagraphs != null && hasShapeTextContent(previewParagraphs)

            return (
              <Fragment key={previewShape.id}>
                <ShapeRenderer shape={previewShape} canvasZoom={scale} />
                {showPreviewText && (
                  <div style={{ opacity: 0.5 }}>
                    <ShapeTextEditor
                      shape={previewShape}
                      paragraphs={previewParagraphs ?? defaultShapeTextParagraphs()}
                      isEditing={false}
                      canvasZoom={scale}
                      onParagraphsChange={() => {}}
                      onExitEdit={() => {}}
                    />
                  </div>
                )}
              </Fragment>
            )
          })}
        </div>
      )}
      {/* Center-origin crosshair: fixed at start point when Alt held during drag (Illustrator-style) */}
      {dragPreviewShape && dragCreate?.altKey && (
        <div
          className="absolute pointer-events-none"
          style={{
            left: dragCreate.startCanvasX,
            top: dragCreate.startCanvasY,
            width: 12,
            height: 12,
            marginLeft: -6,
            marginTop: -6,
            zIndex: 6,
            border: '1px solid var(--editor-bounding-box-accent, #0284C7)',
            borderRadius: '50%',
            backgroundColor: 'transparent',
            boxSizing: 'border-box',
          }}
        >
          <span
            className="absolute bg-[var(--editor-bounding-box-accent,#0284C7)]"
            style={{
              left: '50%',
              top: 0,
              width: 1,
              height: '100%',
              transform: 'translateX(-50%)',
            }}
          />
          <span
            className="absolute bg-[var(--editor-bounding-box-accent,#0284C7)]"
            style={{
              left: 0,
              top: '50%',
              width: '100%',
              height: 1,
              transform: 'translateY(-50%)',
            }}
          />
        </div>
      )}
      {/* Modifier badge when Shift and/or Alt held during shape drag */}
      {dragPreviewShape && (dragCreate?.shiftKey || dragCreate?.altKey) && (
        <div
          className="absolute pointer-events-none flex select-none items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium bg-muted-foreground/90 text-background"
          style={{
            left: dragPreviewShape.x,
            top: dragPreviewShape.y - 22,
            zIndex: 5,
          }}
          aria-label={resolveDragModifierBadgeLabel(dragCreate)}
        >
          {dragCreate?.altKey ? (
            <Globe className="h-3 w-3 shrink-0" strokeWidth={2} aria-hidden />
          ) : null}
          {dragCreate?.shiftKey ? <span className="tabular-nums leading-none">1:1</span> : null}
        </div>
      )}
    </>
  )
}
